import {
  Button,
  DialogActionTrigger,
  DialogTitle,
  Input,
  Text,
  VStack,
  Flex,
  HStack,
  Box,
  Badge,
  Icon,
  Spinner,
  Steps,
} from "@chakra-ui/react";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LuFileOutput, LuFileText, LuCheck } from "react-icons/lu";
import { useState, useCallback, useEffect } from "react";
import {
  TableRowsService,
  StorageService,
  type TableRowCreate,
  type ApiError,
} from "../../client";
import { handleError } from "../../utils";
import useCustomToast from "../../hooks/useCustomToast";
import { useExtractorSSE } from "../../hooks/useExtractorSSE";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "../ui/dialog";
import { Field } from "../ui/field";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { fromZonedTime } from "date-fns-tz";

import { ExtractorService } from "@/client";

// Import custom file upload components
import {
  FileUploadDropzone,
  FileUploadList,
  FileUploadRoot,
} from "../ui/file-upload";

import { ProgressBar } from "../ui/progress-bar";

interface TableField {
  field_id: string;
  field_name: string;
  field_type: string;
  is_required: boolean;
  field_code?: string;
  allowed_extension?: string;
  datetime_type?: string;
  options?: Array<{ label: string; color: string }>;
  description?: string;
}

interface TableFieldOption {
  label: string;
  color: string;
}

interface ExtractedData {
  [key: string]: unknown;
}

interface AddTableRowProps {
  tableId: string;
  disabled?: boolean;
}

const AddTableRowFromSchema = ({
  tableId,
  disabled = false,
}: AddTableRowProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: 파일 업로드, 2: 데이터 추출, 3: 데이터 매핑
  const [isUploading, setIsUploading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "success" | "error"
  >("idle");
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(
    null
  );
  const [mappedData, setMappedData] = useState<Record<string, unknown>>({});
  const [_file, setFile] = useState<File | null>(null);
  const [isDataMapped, setIsDataMapped] = useState(false);
  const [isSaveSuccess, setIsSaveSuccess] = useState(false);
  const [countdown, setCountdown] = useState(3); // 카운트다운 상태 추가
  const [documentId, setDocumentId] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useCustomToast();

  // SSE 훅 사용
  const { connect: connectSSE, disconnect: disconnectSSE } = useExtractorSSE({
    onStatusChange: (_status) => {},
    onComplete: (result) => {
      const extractedData = result?.data
        ? (result.data as ExtractedData)
        : null;
      setExtractedData(extractedData);
      setCurrentStep(3); // 데이터 매핑 단계로 이동
      showSuccessToast("데이터 추출이 완료되었습니다!");
      setIsExtracting(false);
    },
    onError: (error) => {
      showErrorToast(`추출 실패: ${error}`);
      setIsExtracting(false);
    },
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    trigger,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<{ [key: string]: unknown }>({
    mode: "onChange",
    criteriaMode: "all",
  });

  // ✅ 필드 메타데이터 조회
  const { data, isLoading: isMetaLoading } = useQuery({
    queryKey: ["openSearchDocs", tableId],
    queryFn: () =>
      TableRowsService.readTableRows({ tableId, skip: 0, limit: 1 }),
    enabled: !disabled && !!tableId,
  });

  const mutation = useMutation({
    mutationFn: (data: TableRowCreate) => {
      return TableRowsService.createTableRow({ tableId, requestBody: data });
    },
    onSuccess: () => {
      showSuccessToast("데이터가 성공적으로 추가되었습니다.");
      setIsSaveSuccess(true);
      setCountdown(3); // 카운트다운 초기화

      const intervalId = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            clearInterval(intervalId);
            // 카운트다운 종료 후 다이얼로그 닫기 및 상태 초기화
            reset();
            setFile(null);
            setUploadProgress(0);
            setUploadStatus("idle");
            setExtractedData(null);
            setMappedData({});
            setCurrentStep(1);
            setIsSaveSuccess(false);
            setIsOpen(false);
            return 0; // 또는 다른 적절한 값
          }
          return prevCountdown - 1;
        });
      }, 1000);
    },

    onError: (err) => {
      handleError(err as ApiError);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "openSearchDocs" &&
          typeof query.queryKey[1] === "object" &&
          (query.queryKey[1] as { tableId: string }).tableId === tableId,
      });
    },
  });

  const onSubmit = (data: Record<string, unknown>) => {
    mutation.mutate({ data });
  };

  // 파일 업로드 핸들러
  const handleFileUpload = useCallback(
    async (file: File) => {
      if (isUploading) return;
      setIsUploading(true);
      setUploadProgress(0);
      setUploadStatus("uploading");

      try {
        // StorageService를 사용하여 파일 업로드
        const response = await StorageService.uploadFiles({
          formData: {
            context_id: tableId,
            files: [file],
            use_schema: "true",
          },
        });

        // 응답을 Record<string, unknown>으로 캐스팅하여 처리
        const result = response as Record<string, unknown>;
        console.log("StorageService.uploadFiles 응답:", result);

        if (
          result.documents &&
          Array.isArray(result.documents) &&
          result.documents.length > 0
        ) {
          const uploadedDoc = result.documents[0] as Record<string, unknown>;
          const docId = String(uploadedDoc.id);
          setDocumentId(docId);
          setFile(file);
          setUploadStatus("success");
          setUploadProgress(100);
        } else {
          setUploadStatus("error");
          showErrorToast("파일 업로드에 실패했습니다.");
        }
      } catch (error) {
        setUploadStatus("error");
        showErrorToast("파일 업로드에 실패했습니다.");
      } finally {
        setIsUploading(false);
      }
    },
    [isUploading, tableId, showErrorToast]
  );

  // 데이터 추출 핸들러
  const handleDataExtraction = useCallback(async () => {
    if (!_file) {
      showErrorToast("업로드된 파일이 없습니다.");
      return;
    }

    setIsExtracting(true);

    try {
      // 파일을 base64로 변환
      const reader = new FileReader();
      reader.onload = async () => {
        const response = await ExtractorService.extractSchema({
          documentId: documentId ? String(documentId) : "temp",
          requestBody: {
            file: {
              name: _file?.name ?? "",
              data: reader.result as string, // base64 string
            },
            schema_fields: (
              (data?.fields as unknown as TableField[]) || []
            ).map((field) => ({
              name: field.field_name,
              type: field.field_type,
              description: field.description || field.field_name,
            })),
            user_id: localStorage.getItem("user_id"),
            table_id: tableId,
          },
        });

        // 응답을 Record<string, unknown>으로 캐스팅하여 처리
        const result = response as Record<string, unknown>;

        // task_result에서 상태 정보 확인
        const taskReady = result.ready as boolean;
        const taskSuccessful = result.successful as boolean;
        const taskFailed = result.failed as boolean;

        if (taskReady) {
          if (taskSuccessful && result.result) {
            // 즉시 결과 처리
            const extractedData =
              (result.result as Record<string, unknown>)?.data || result.result;
            setExtractedData(extractedData as ExtractedData);
            setCurrentStep(3); // 데이터 매핑 단계로 이동
            showSuccessToast("데이터 추출이 완료되었습니다!");
            setIsExtracting(false);
            connectSSE(result.task_id as string);
            return;
          }

          if (taskFailed) {
            // 즉시 오류 처리
            const errorMessage =
              (result.error as string) || "데이터 추출에 실패했습니다.";
            showErrorToast(`추출 실패: ${errorMessage}`);
            setIsExtracting(false);
            return;
          }
        }

        // 태스크가 진행 중인 경우 SSE 연결 시작
        connectSSE(result.task_id as string);
      };

      reader.onerror = () => {
        showErrorToast("파일을 읽는 중 오류가 발생했습니다.");
        setIsExtracting(false);
      };

      reader.readAsDataURL(_file);
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : "데이터 추출에 실패했습니다."
      );
      setIsExtracting(false);
    }
  }, [
    _file,
    data?.fields,
    showErrorToast,
    showSuccessToast,
    connectSSE,
    tableId,
    documentId,
  ]);

  // 데이터 매핑 핸들러
  const handleDataMapping = useCallback(() => {
    if (!extractedData || !data?.fields) {
      return;
    }

    const fields = data.fields as unknown as TableField[];
    const mapped: Record<string, unknown> = {};

    // 추출된 실제 데이터 객체
    const parsed =
      typeof extractedData === "object" &&
      extractedData !== null &&
      "data" in extractedData &&
      typeof extractedData.data === "object" &&
      extractedData.data !== null
        ? (extractedData.data as Record<string, unknown>)
        : extractedData; // 이미 data 객체일 수 있음

    for (const field of fields) {
      let extractedValue = parsed[field.field_name];
      if (
        extractedValue === undefined ||
        extractedValue === null ||
        extractedValue === ""
      ) {
        extractedValue = "N/A";
      }
      mapped[field.field_name] = extractedValue;
      setValue(field.field_name, extractedValue);
    }

    setMappedData(mapped);
    setIsDataMapped(true);
  }, [extractedData, data?.fields, setValue]);

  // 컴포넌트 언마운트 시 SSE 연결 정리
  const cleanup = useCallback(() => {
    disconnectSSE();
  }, [disconnectSSE]);

  // 다이얼로그가 닫힐 때 상태 초기화
  const handleDialogClose = useCallback(() => {
    cleanup();
    setCurrentStep(1);
    setUploadStatus("idle");
    setExtractedData(null);
    setMappedData({});
    setIsExtracting(false);
    setIsSaveSuccess(false);
  }, [cleanup]);

  // 데이터 매핑이 완료되면 자동으로 매핑 실행
  useEffect(() => {
    if (currentStep === 3 && extractedData && data?.fields) {
      handleDataMapping();
    }
  }, [currentStep, extractedData, data?.fields, handleDataMapping]);

  function getContrastColor(bgColor: string) {
    const color = bgColor.charAt(0) === "#" ? bgColor.substring(1, 7) : bgColor;
    const r = Number.parseInt(color.substring(0, 2), 16);
    const g = Number.parseInt(color.substring(2, 4), 16);
    const b = Number.parseInt(color.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? "#000000" : "#FFFFFF";
  }

  const renderInputByType = (field: TableField) => {
    const { field_type, field_name, datetime_type } = field;

    switch (field_type) {
      case "number":
        return (
          <Input
            placeholder={field_name}
            type="number"
            {...register(field_name, {
              required: field.is_required
                ? `${field_name}는(은) 필수 입력 필드입니다.`
                : false,
              valueAsNumber: true,
            })}
          />
        );

      case "string":
        return (
          <Input
            placeholder={field_name}
            type="text"
            {...register(field_name, {
              required: field.is_required
                ? `${field_name}는(은) 필수 입력 필드입니다.`
                : false,
            })}
          />
        );

      case "datetime": {
        const dtType = datetime_type || "datetime";

        return (
          <Controller
            control={control}
            name={field_name}
            rules={{
              required: field.is_required
                ? `${field_name}는(은) 필수 입력 필드입니다.`
                : false,
            }}
            render={({ field: { onChange, value, ref } }) => {
              // 안전한 날짜 변환 함수
              const getSafeDate = (dateValue: unknown): Date | null => {
                if (!dateValue) return null;

                try {
                  const date = new Date(dateValue as string);
                  // 유효한 날짜인지 확인
                  return Number.isNaN(date.getTime()) ? null : date;
                } catch (error) {
                  return null;
                }
              };

              return (
                <ReactDatePicker
                  selected={getSafeDate(value)}
                  onChange={(date: Date | null) => {
                    if (!date) {
                      onChange(null);
                      return;
                    }

                    let formatted: string;
                    if (dtType === "date") {
                      formatted = format(date, "yyyy-MM-dd");
                    } else if (dtType === "time") {
                      formatted = `1970-01-01T${format(date, "HH:mm:ss")}`;
                    } else {
                      const utcDate = fromZonedTime(date, "Asia/Seoul");
                      formatted = format(utcDate, "yyyy-MM-dd'T'HH:mm:ss");
                    }

                    onChange(formatted);
                  }}
                  showTimeSelect={dtType !== "date"}
                  showTimeSelectOnly={dtType === "time"}
                  dateFormat={
                    dtType === "date"
                      ? "yyyy/MM/dd"
                      : dtType === "time"
                        ? "HH:mm"
                        : "yyyy/MM/dd HH:mm"
                  }
                  placeholderText={
                    dtType === "date"
                      ? "YYYY/MM/DD"
                      : dtType === "time"
                        ? "HH:mm"
                        : "YYYY/MM/DD HH:mm"
                  }
                  customInput={
                    <Input
                      ref={ref}
                      placeholder={field_name}
                      borderColor="gray.300"
                      border="1px solid"
                    />
                  }
                />
              );
            }}
          />
        );
      }

      case "selection":
        return (
          <Controller
            control={control}
            name={field_name}
            rules={{
              required: field.is_required
                ? `${field_name}는(은) 필수 입력 필드입니다.`
                : false,
            }}
            render={({ field: { onChange, value = "" } }) => (
              <Flex wrap="wrap" gap={2}>
                {(field.options || []).map(
                  (opt: TableFieldOption, i: number) => {
                    const isSelected = value === opt.label;
                    const textColor = getContrastColor(opt.color);

                    return (
                      <Button
                        key={`${opt.label}-${i}`}
                        onClick={() => onChange(opt.label)}
                        size="sm"
                        variant="outline"
                        bg={opt.color}
                        color={textColor}
                        border={
                          isSelected
                            ? "3px solid #2B6CB0"
                            : "2px solid transparent"
                        }
                        boxShadow={isSelected ? "0 0 0 2px #90CDF4" : "none"}
                        fontWeight={isSelected ? "bold" : "normal"}
                        opacity={isSelected ? 1 : 0.8}
                        _hover={{
                          opacity: 0.95,
                          transform: "scale(1.03)",
                        }}
                        _active={{ transform: "scale(0.97)" }}
                      >
                        {opt.label}
                      </Button>
                    );
                  }
                )}
              </Flex>
            )}
          />
        );

      case "file":
        return (
          <Controller
            control={control}
            name={field_name}
            rules={{
              required: field.is_required
                ? `${field_name}는(은) 필수 입력 필드입니다.`
                : false,
            }}
            render={({ field: { onChange } }) => (
              <Field>
                <FileUploadRoot
                  maxW="xl"
                  alignItems="stretch"
                  maxFiles={1}
                  inputProps={{
                    accept: field.allowed_extension
                      ? `.${field.allowed_extension}`
                      : undefined,
                    onChange: async (e) => {
                      const files = e.target.files;
                      if (!files || files.length === 0) return;

                      const file = files[0];

                      try {
                        // StorageService를 사용하여 파일 업로드
                        const response = await StorageService.uploadFiles({
                          formData: {
                            context_id: tableId,
                            files: [file],
                            use_schema: "true",
                          },
                        });

                        // 응답을 Record<string, unknown>으로 캐스팅하여 처리
                        const result = response as Record<string, unknown>;
                        console.log("StorageService.uploadFiles 응답:", result);

                        if (
                          result.documents &&
                          Array.isArray(result.documents) &&
                          result.documents.length > 0
                        ) {
                          const uploadedDoc = result.documents[0] as Record<
                            string,
                            unknown
                          >;
                          const documentId = String(uploadedDoc.id);

                          onChange({
                            file_metadata: {
                              object_id: documentId,
                              bucket: "default", // 기본 버킷 사용
                            },
                          });
                          void trigger(field_name);
                          e.target.value = "";
                        } else {
                          showErrorToast("파일 업로드에 실패했습니다.");
                        }
                      } catch (error) {
                        showErrorToast("파일 업로드에 실패했습니다.");
                      }
                    },
                  }}
                >
                  <FileUploadDropzone
                    label="파일을 드래그하거나 클릭해서 업로드하세요"
                    description={`지원되는 형식: ${field.allowed_extension?.toUpperCase() || "모든 파일"}`}
                  />
                  <FileUploadList showSize={true} />
                </FileUploadRoot>
              </Field>
            )}
          />
        );

      default:
        return <Input placeholder="지원되지 않는 필드 타입입니다." disabled />;
    }
  };

  // Steps 컴포넌트용 단계 정의
  const steps = [
    {
      title: "문서 업로드",
    },
    {
      title: "데이터 추출",
    },
    {
      title: "데이터 매핑 및 저장",
    },
  ];

  return (
    <DialogRoot
      size={{ base: "xs", md: "lg" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => {
        setIsOpen(open);
        if (!open) {
          handleDialogClose();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button my={4} disabled={disabled}>
          <LuFileOutput fontSize="16px" />
          스키마 기반 데이터 추출
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>스키마 기반 데이터 추출</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Steps.Root
            count={steps.length}
            size="sm"
            colorPalette="blue"
            step={isSaveSuccess ? steps.length : currentStep - 1}
            onStepChange={({ step }) => {
              if (!isSaveSuccess) {
                setCurrentStep(step + 1);
              }
            }}
          >
            <Steps.List>
              {steps.map((stepItem, index) => (
                <Steps.Item
                  key={`step-${index}-${stepItem.title}`}
                  index={index}
                >
                  <Steps.Indicator>
                    <Steps.Status
                      incomplete={index + 1}
                      complete={<LuCheck />}
                    />
                  </Steps.Indicator>
                  <Steps.Separator />
                </Steps.Item>
              ))}
            </Steps.List>

            {/* 단계별 콘텐츠 (저장 성공 시에는 보이지 않음) */}
            {!isSaveSuccess && (
              <>
                <Steps.Content index={0} width="full">
                  <VStack gap={4} mt={6} width="full">
                    <FileUploadRoot
                      maxW="xl"
                      alignItems="stretch"
                      maxFiles={1}
                      inputProps={{
                        accept: ".pdf,.doc,.docx,.txt,.csv",
                        onChange: async (e) => {
                          const files = e.target.files;
                          if (!files || files.length === 0) return;
                          await handleFileUpload(files[0]);
                        },
                      }}
                    >
                      <FileUploadDropzone
                        label="문서를 드래그하거나 클릭해서 업로드하세요"
                        description="지원되는 형식: PDF, DOC, DOCX, TXT, CSV"
                      />
                      <FileUploadList showSize={true} />
                    </FileUploadRoot>

                    {uploadStatus === "uploading" && (
                      <ProgressBar
                        value={uploadProgress}
                        label="업로드 중..."
                      />
                    )}
                    {uploadStatus === "success" && (
                      <Box
                        p={3}
                        bg="green.50"
                        borderWidth={1}
                        borderColor="green.200"
                        borderRadius="md"
                        color="green.800"
                        width="full"
                      >
                        <HStack>
                          <Icon as={LuCheck} color="green.500" />
                          <Text>파일 업로드가 완료되었습니다.</Text>
                        </HStack>
                      </Box>
                    )}
                    {uploadStatus === "success" && (
                      <Button
                        onClick={() => setCurrentStep(2)}
                        colorScheme="blue"
                        width="full"
                      >
                        다음
                      </Button>
                    )}
                    {uploadStatus === "error" && (
                      <Box
                        p={3}
                        bg="red.50"
                        borderWidth={1}
                        borderColor="red.200"
                        borderRadius="md"
                        color="red.800"
                        width="full"
                      >
                        <Text>파일 업로드에 실패했습니다.</Text>
                      </Box>
                    )}
                  </VStack>
                </Steps.Content>

                <Steps.Content index={1} width="full">
                  <VStack gap={4} mt={6} width="full">
                    {isExtracting ? (
                      <Box textAlign="center" py={8} width="full">
                        <Spinner size="xl" mb={4} />
                        <Text fontSize="lg" fontWeight="bold" mb={2}>
                          데이터 추출 중...
                        </Text>
                        <Text color="gray.600" mb={4}>
                          업로드된 문서에서 스키마 기반으로 데이터를 추출하고
                          있습니다. 잠시만 기다려주세요.
                        </Text>
                      </Box>
                    ) : (
                      <>
                        <Box textAlign="center" py={8} width="full">
                          <Icon
                            as={LuFileText}
                            w={12}
                            h={12}
                            mb={4}
                            color="blue.500"
                          />
                          <Text fontSize="lg" fontWeight="bold" mb={2}>
                            파일 준비 완료
                          </Text>
                          <Text color="gray.600">
                            문서가 성공적으로 업로드되었습니다. 이제 데이터
                            추출을 시작할 수 있습니다.
                          </Text>
                        </Box>
                        <Button
                          onClick={handleDataExtraction}
                          disabled={!_file || isExtracting}
                          loading={isExtracting}
                          colorScheme="blue"
                        >
                          <Icon as={LuFileText} mr={2} />
                          데이터 추출 시작
                        </Button>
                      </>
                    )}
                  </VStack>
                </Steps.Content>

                <Steps.Content index={2} width="full">
                  <VStack gap={4} mt={6} width="full">
                    {extractedData && (
                      <Box
                        p={3}
                        bg="green.50"
                        borderWidth={1}
                        borderColor="green.200"
                        borderRadius="md"
                        color="green.800"
                        width="full"
                      >
                        <HStack>
                          <Icon as={LuCheck} color="green.500" />
                          <Text>
                            데이터 추출이 완료되었습니다. 아래 필드에 매핑된
                            데이터를 확인하세요.
                          </Text>
                        </HStack>
                      </Box>
                    )}

                    <form
                      onSubmit={handleSubmit(onSubmit)}
                      style={{ width: "100%" }}
                    >
                      <VStack gap={4} width="full">
                        {!isMetaLoading &&
                          (
                            data?.fields as unknown as TableField[] | undefined
                          )?.map((field) => {
                            const isMapped =
                              mappedData[field.field_name] !== undefined;
                            return (
                              <Field
                                key={field.field_id}
                                required={field.is_required}
                                invalid={!!errors[field.field_name]}
                                errorText={
                                  errors[
                                    field.field_name
                                  ]?.message?.toString() ?? ""
                                }
                                label={
                                  <HStack>
                                    <Text>{field.field_name}</Text>
                                    {isMapped && (
                                      <Badge colorScheme="green" size="sm">
                                        <HStack gap={1}>
                                          <Icon as={LuCheck} />
                                          <Text>매핑됨</Text>
                                        </HStack>
                                      </Badge>
                                    )}
                                  </HStack>
                                }
                                width="full"
                              >
                                {renderInputByType(field)}
                              </Field>
                            );
                          })}
                        {/* 버튼을 HStack으로 나란히 배치 */}
                        <HStack width="full" justify="flex-end" mt={4}>
                          <DialogActionTrigger asChild>
                            <Button
                              variant="subtle"
                              colorPalette="gray"
                              disabled={isSubmitting || isExtracting}
                            >
                              취소
                            </Button>
                          </DialogActionTrigger>
                          <Button
                            variant="solid"
                            disabled={!isDataMapped || isSubmitting}
                            loading={isSubmitting}
                            onClick={handleSubmit(onSubmit)}
                          >
                            저장
                          </Button>
                        </HStack>
                      </VStack>
                    </form>
                  </VStack>
                </Steps.Content>
              </>
            )}
          </Steps.Root>

          {/* 저장 성공 메시지 (저장 성공 시에만 보임) */}
          {isSaveSuccess && (
            // <Steps.Content>를 제거하고 VStack을 직접 사용합니다.
            <VStack gap={4} mt={6} width="full" textAlign="center" py={8}>
              <Icon as={LuCheck} w={16} h={16} color="green.500" />
              <Text fontSize="xl" fontWeight="bold" color="green.600">
                저장 완료!
              </Text>
              <Text color="gray.600" fontSize="lg">
                데이터가 성공적으로 저장되었습니다.
              </Text>
              <Text color="gray.500" fontSize="sm">
                {countdown}초 후 자동으로 닫힙니다.
              </Text>
            </VStack>
          )}
        </DialogBody>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export default AddTableRowFromSchema;
